/**
 * AskAiFab — global floating "Ask SnapOps AI" button.
 *
 * Mounted at layout root, visible on every authenticated screen for
 * Studio/Pro/Trial/Grandfathered plans. Free plan renders nothing.
 * Opens a right-side Sheet with a chat-style Ask AI surface that
 * posts to POST /api/ai/ask (natural-language KPI answers). This
 * component replaces the old dashboard-only AskAiCard.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

// Format a rupee amount consistent with the rest of the app.
function fmtINR(n) {
  if (n === null || n === undefined) return "—";
  try {
    return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  } catch {
    return "₹" + n;
  }
}

const SUGGESTIONS = [
  "What's my profit this month?",
  "How many leads did I get today?",
  "Any overdue deliveries?",
  "What's my total sale value this month?",
];

export default function AskAiFab() {
  const { studio } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role, text, data?, query_type? }
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const scrollRef = useRef(null);

  const plan = (studio?.plan || "free").toLowerCase();
  const enabled = plan !== "free";

  useEffect(() => {
    if (!open || !enabled) return;
    (async () => {
      try {
        const r = await api.get("/ai/quota");
        setQuota(r.data);
      } catch (_) { /* non-critical */ }
    })();
  }, [open, enabled]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const headlineFor = (d) => {
    if (!d) return null;
    if (d.profit_inr !== undefined) return { label: "Profit", val: fmtINR(d.profit_inr) };
    if (d.amount_inr !== undefined) return { label: "Amount", val: fmtINR(d.amount_inr) };
    if (d.total_inr !== undefined)  return { label: "Total",  val: fmtINR(d.total_inr) };
    if (typeof d.count === "number") return { label: "Count", val: d.count.toLocaleString("en-IN") };
    if (typeof d.total === "number") return { label: "Total", val: d.total.toLocaleString("en-IN") };
    return null;
  };

  const send = async (overrideText) => {
    const q = (overrideText ?? input).trim();
    if (!q || loading) return;
    if (!overrideText) setInput("");
    setMessages((prev) => [...prev, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { query: q });
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: data.answer || "",
        data: data.data,
        query_type: data.query_type,
      }]);
      try {
        const r = await api.get("/ai/quota");
        setQuota(r.data);
      } catch (_) { /* ignore */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't get an answer right now.";
      setMessages((prev) => [...prev, { role: "assistant", text: detail }]);
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) return null;
  // On /help the full-page AI Navigator already provides an AI surface —
  // the FAB and its composer would duplicate function and visually collide
  // with the page's own composer on mobile.
  if (location.pathname === "/help") return null;

  return (
    <>
      <button
        data-testid="ask-ai-fab"
        onClick={() => setOpen(true)}
        aria-label="Ask SnapOps AI"
        className="fixed z-40 inline-flex items-center gap-2 transition-colors"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
          right: 20,
          background: "linear-gradient(135deg,#C8A063,#A87C42)",
          color: "#FFF7E8",
          borderRadius: 999,
          fontFamily: "var(--ds-body-font)",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.04em",
          padding: "12px 18px",
          border: "none",
          boxShadow: "0 8px 22px rgba(168,124,66,.42), 0 0 0 1px rgba(255,255,255,.14) inset",
          cursor: "pointer",
        }}
      >
        <Sparkles size={16} strokeWidth={2.2} />
        <span>Ask AI</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:w-[460px] sm:max-w-[460px] flex flex-col p-0"
          data-testid="ask-ai-drawer"
        >
          <SheetHeader
            className="p-6 pb-3 border-b"
            style={{ borderColor: "rgba(34,56,44,0.10)" }}
          >
            <SheetTitle className="font-display text-2xl" style={{ color: "var(--brand-ink)" }}>
              Ask SnapOps AI
            </SheetTitle>
            <p className="text-xs mt-1" style={{ color: "var(--brand-muted)" }}>
              Profit, tasks, projects, follow-ups — in plain words.
              {quota?.remaining ? (
                <span data-testid="ask-ai-quota" className="ml-2 opacity-70">
                  · {quota.remaining.ask} left today
                </span>
              ) : null}
            </p>
          </SheetHeader>

          <div
            ref={scrollRef}
            data-testid="ask-ai-messages"
            className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
            style={{ background: "#F9F5EC" }}
          >
            {messages.length === 0 && (
              <div className="space-y-2" data-testid="ask-ai-empty">
                <p className="text-sm" style={{ color: "var(--brand-muted)", fontStyle: "italic" }}>
                  Try asking:
                </p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`ask-ai-suggestion-${s.slice(0, 20)}`}
                    onClick={() => send(s)}
                    className="block w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white transition-colors"
                    style={{ background: "#FFFDFA", border: "1px solid rgba(34,56,44,0.08)" }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => {
              const headline = m.role === "assistant" ? headlineFor(m.data) : null;
              return (
                <div
                  key={i}
                  data-testid={`ask-ai-msg-${m.role}`}
                  className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                >
                  <div
                    className="rounded-2xl px-4 py-3 text-sm max-w-[88%]"
                    style={{
                      background: m.role === "user" ? "var(--ds-ink)" : "#FFFDFA",
                      color: m.role === "user" ? "#F7F3EC" : "var(--brand-ink)",
                      border: m.role === "user" ? "none" : "1px solid rgba(34,56,44,0.10)",
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {m.text}
                    {headline && (
                      <div
                        data-testid="ask-ai-msg-stat"
                        className="mt-3 inline-flex items-baseline gap-2"
                        style={{
                          background: "#F6EFDF",
                          border: "1px solid rgba(176,141,85,0.22)",
                          borderRadius: 10,
                          padding: "6px 12px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 9, fontWeight: 800, letterSpacing: "0.14em",
                            color: "#95643C", textTransform: "uppercase",
                          }}
                        >
                          {headline.label}
                        </span>
                        <span
                          style={{
                            fontFamily: "var(--ds-num-font)", fontSize: 18, fontWeight: 500,
                            color: "var(--brand-ink)",
                          }}
                        >
                          {headline.val}
                        </span>
                      </div>
                    )}
                    {m.role === "assistant" && m.query_type && (
                      <div
                        style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                          color: "rgba(34,56,44,0.4)", marginTop: 6, textTransform: "uppercase",
                        }}
                      >
                        {m.query_type.replace(/_/g, " ")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="flex justify-start" data-testid="ask-ai-loading">
                <div
                  className="rounded-2xl px-4 py-3 text-sm"
                  style={{
                    background: "#FFFDFA",
                    color: "var(--brand-muted)",
                    border: "1px solid rgba(34,56,44,0.10)",
                  }}
                >
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-4 border-t"
            style={{ borderColor: "rgba(34,56,44,0.10)", background: "#FFFDFA" }}
          >
            <div className="flex gap-2">
              <input
                data-testid="ask-ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="What's my profit this month?"
                disabled={loading}
                className="flex-1 px-3 py-2.5 rounded-lg outline-none text-sm"
                style={{
                  background: "#F7F3EC",
                  border: "1px solid rgba(34,56,44,0.10)",
                  color: "var(--brand-ink)",
                  minHeight: 44,
                }}
              />
              <button
                type="submit"
                data-testid="ask-ai-submit"
                disabled={!input.trim() || loading}
                className="px-4 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-40"
                style={{
                  background: "var(--ds-ink)",
                  color: "#F7F3EC",
                  border: "none",
                  minHeight: 44,
                  minWidth: 68,
                }}
              >
                Ask
              </button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
